import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Grid, Divider,
  CircularProgress, IconButton, Menu, MenuItem, Tooltip, Alert, Chip, Button,
  Badge as CountBadge, Popover, ButtonBase,
} from '@mui/material';
import {
  AccountBalanceWallet, AccountCircle, People, Receipt,
  Warning, PendingActions, Description, TrendingUp,
  School, LocalShipping, Person, Badge, Refresh,
  HomeWork, Settings, Groups, MonetizationOn,
  PersonalVideoTwoTone, MailOutline, FactCheck,
  Add, AccountBalance, ArrowForward, TrendingDown, ChevronRight, Search,
} from '@mui/icons-material';
import axios from 'axios';
import { fmt, fmtN, sym } from '../utils/currency';
import { canView } from '../utils/permissions';
import { TOP_MODULES } from '../config/moduleNav';
import TopModuleBar from './TopModuleBar';
import { openGlobalSearch } from './GlobalSearch';

const API = process.env.REACT_APP_API_URL;

// ── Quick-Create mega-menu (QuickBooks-style "+ Create") ────────────────────
// Each item routes to the best available create screen; where no dedicated
// /new form exists the item opens that module's list (where "Add" lives).
// Items are gated by the same permission module their backend route enforces,
// and a whole column is hidden when the user can create none of its items.
const QUICK_CREATE = [
  { title: 'Learners & Fees', color: '#1565c0', items: [
    { label: 'Admit Learner',   path: '/learners',                   module: 'academics' },
    { label: 'Fee Invoice',     path: '/billing',                    module: 'finance'   },
    { label: 'Receive Payment', path: '/payments/receipt',           module: 'finance'   },
    { label: 'Batch Receipt',   path: '/finance/batch-receipts/new', module: 'finance'   },
    { label: 'Credit Note',     path: '/finance/credit-notes',       module: 'finance'   },
    { label: 'Statement',       path: '/statements',                 module: 'finance'   },
  ] },
  { title: 'Suppliers & Purchases', color: '#e65100', items: [
    { label: 'Purchase Bill',     path: '/payables/invoices',         module: 'supply-chain' },
    { label: 'Payment Voucher',   path: '/payables/vouchers/new',     module: 'supply-chain' },
    { label: 'Purchase Order',    path: '/payables/orders/new',       module: 'supply-chain' },
    { label: 'Store Requisition', path: '/payables/requisitions/new', module: 'supply-chain' },
    { label: 'Add Supplier',      path: '/suppliers',                 module: 'supply-chain' },
  ] },
  { title: 'General Ledger', color: '#37474f', items: [
    { label: 'Journal Entry', path: '/finance/general-journal', module: 'finance' },
    { label: 'GL Account',    path: '/gl-accounts',             module: 'finance' },
    { label: 'Bank Account',  path: '/banks',                   module: 'finance' },
    { label: 'Budget Entry',  path: '/finance/budgets',         module: 'finance' },
  ] },
  { title: 'People', color: '#6a1b9a', items: [
    { label: 'Add Employee',      path: '/hr/employees',              module: 'hr' },
    { label: 'Leave Application', path: '/hr/leave/applications/new', module: 'hr' },
    { label: 'Appraisal',         path: '/hr/appraisal',              module: 'hr' },
  ] },
  { title: 'Academics', color: '#2e7d32', items: [
    { label: 'Academic Year',    path: '/academics/years',            module: 'academics' },
    { label: 'Class',            path: '/academics/classes',          module: 'academics' },
    { label: 'Fee Template',     path: '/academics/fee-templates',    module: 'academics' },
    { label: 'Assessment Setup', path: '/academics/assessment-setup', module: 'academics' },
    { label: 'School Event',     path: '/academics/events',           module: 'academics' },
  ] },
];

// Palette for the expense donut slices (top categories + "Other").
const DONUT_COLORS = ['#1565c0', '#e65100', '#2e7d32', '#6a1b9a', '#00838f', '#90a4ae'];

const Dashboard = () => {
  const navigate   = useNavigate();
  const [kpis,     setKpis]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [unread,   setUnread]   = useState(0);
  const [approvals,setApprovals]= useState(0);
  const [ctStats,  setCtStats]  = useState(null); // class-teacher activity stats
  const [quickAnchor, setQuickAnchor] = useState(null); // quick-create popover
  const [banks,    setBanks]    = useState(null);  // bank/cash accounts + balances
  const [incStmt,  setIncStmt]  = useState(null);  // income statement (revenue/expense)
  const [finLoading, setFinLoading] = useState(false);
  const [period,   setPeriod]   = useState('year'); // 'month' | 'year' for the snapshot
  const [school,   setSchool]   = useState(JSON.parse(localStorage.getItem('school') || 'null'));
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchKpis = useCallback(async () => {
    // A super-admin who hasn't opened a school has no client DB — send them to
    // the schools admin page. When impersonating (opened a school), let them in.
    if (user?.isSuperAdmin && !user?.impersonating) { navigate('/admin/schools'); return; }
    if (user?.role === 'parent') { navigate('/parent'); return; }   // parents use the parent portal
    if (!user?.role) { navigate('/login'); return; }
    setLoading(true); setError('');
    try {
      // Only fetch KPIs for modules this user can actually view — otherwise every
      // module they lack access to logs an avoidable 403 (the cards are already
      // hidden below, but the underlying requests were still firing regardless).
      const canAcademics = canView('academics');
      const canFinance   = canView('finance');
      const canSupply    = canView('supply-chain');
      const canHr        = canView('hr');
      const skipPage = Promise.resolve({ data: { data: [] } }); // paginated {data,total,...} endpoints
      const skipList = Promise.resolve({ data: [] });           // plain-array endpoints

      const [lR, invR, recR, pinvR, pvR, learnerR, empR] = await Promise.allSettled([
        canAcademics ? axios.get(`${API}/api/learners`,          { headers }) : skipPage,
        canFinance   ? axios.get(`${API}/api/finance/invoices`,  { headers }) : skipPage,
        canFinance   ? axios.get(`${API}/api/finance/payments`,  { headers }) : skipPage,
        canSupply    ? axios.get(`${API}/api/payables/invoices`, { headers }) : skipList,
        canSupply    ? axios.get(`${API}/api/payables/vouchers`, { headers }) : skipList,
        canAcademics ? axios.get(`${API}/api/academics/classes`, { headers }) : skipList,
        canHr        ? axios.get(`${API}/api/employees`,         { headers }) : skipList,
      ]);
      const get  = (r) => r.status === 'fulfilled' ? r.value.data : [];
      const now  = new Date();
      const mStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const invs = invR.status === 'fulfilled' ? (invR.value.data?.data || []) : [];
      const lnrs = lR.status  === 'fulfilled' ? (lR.value.data?.data  || []) : [];
      const recs = recR.status === 'fulfilled' ? (recR.value.data?.data || []) : [];
      const openInv   = invs.filter(i => ['Open', 'Partial'].includes(i.status));
      const overdue   = openInv.filter(i => i.dueDate && new Date(i.dueDate) < now);
      const monthRec  = recs.filter(r => new Date(r.paymentDate || r.createdAt) >= mStart);
      const openPinv  = get(pinvR).filter(i => i.status !== 'Paid' && i.status !== 'Cancelled');
      const pendingPv = get(pvR).filter(v => v.status === 'Open' || v.status === 'Draft');
      setKpis({
        learners:      lnrs.length,
        openInv:       openInv.length,
        openInvAmt:    openInv.reduce((s, i) => s + ((i.amount||0)-(i.paid||0)), 0),
        overdue:       overdue.length,
        monthRecAmt:   monthRec.reduce((s, r) => s + (r.amount||0), 0),
        monthRecCount: monthRec.length,
        openPinv:      openPinv.length,
        openPinvAmt:   openPinv.reduce((s, i) => s + ((i.totalAmount||0)-(i.paid||0)-(i.credited||0)), 0),
        pendingPv:     pendingPv.length,
        classes:       get(learnerR).length,
        employees:     get(empR).filter(e => e.status === 'Active').length,
      });
    } catch { setError('Failed to load KPIs'); }
    finally { setLoading(false); }
  }, [user?.role, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchKpis(); }, [fetchKpis]);

  // Class teachers have no module permissions, so the KPI cues above stay empty.
  // Pull a small class summary instead so their Activities row shows real stats.
  useEffect(() => {
    if (user?.role !== 'classTeacher') return;
    (async () => {
      try {
        const { data: classes } = await axios.get(`${API}/api/academics/class-teacher/classes`, { headers });
        if (!classes?.length) { setCtStats({ classCount: 0, learners: 0, balance: null, results: 0, className: '' }); return; }
        const primary = classes[0];
        const [lRes, fRes, rRes] = await Promise.allSettled([
          axios.get(`${API}/api/academics/class-teacher/learners`,        { headers, params: { classCode: primary.classCode } }),
          axios.get(`${API}/api/academics/class-teacher/finance`,         { headers, params: { classCode: primary.classCode } }),
          axios.get(`${API}/api/academics/class-teacher/academic-results`,{ headers, params: { classCode: primary.classCode } }),
        ]);
        setCtStats({
          classCount: classes.length,
          className:  primary.className,
          learners:   lRes.status === 'fulfilled' ? (lRes.value.data?.length || 0) : 0,
          balance:    fRes.status === 'fulfilled' ? (fRes.value.data?.totalBalance ?? null) : null,
          results:    rRes.status === 'fulfilled' ? (rRes.value.data?.length || 0) : 0,
        });
      } catch { setCtStats(null); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  // Financial snapshot — Bank Accounts balances + Income vs Expense for the
  // selected period. Finance-only; skipped entirely for users without access
  // so we don't fire avoidable 403s (matches the KPI gating above).
  useEffect(() => {
    if (!canView('finance')) return;
    const now  = new Date();
    const from = period === 'month'
      ? new Date(now.getFullYear(), now.getMonth(), 1)
      : new Date(now.getFullYear(), 0, 1);
    // Format from local Y-M-D parts — toISOString() would shift to UTC and,
    // in UTC+ zones (e.g. Tanzania), push "Jan 1" back into the prior year.
    const iso  = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    setFinLoading(true);
    Promise.allSettled([
      axios.get(`${API}/api/finance/banks`, { headers }),
      axios.get(`${API}/api/finance/reports/income-statement`,
        { headers, params: { from: iso(from), to: iso(now) } }),
    ]).then(([bR, iR]) => {
      setBanks(bR.status === 'fulfilled' ? (bR.value.data || []) : []);
      setIncStmt(iR.status === 'fulfilled' ? iR.value.data : null);
    }).finally(() => setFinLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  // Unread mail badge — messages sent to my email (e.g. parents writing to me)
  useEffect(() => {
    axios.get(`${API}/api/messages/unread-count`, { headers })
      .then(r => setUnread(r.data?.count || 0))
      .catch(() => {});
    // Pending approvals waiting on me (documents routed to me or my group)
    axios.get(`${API}/api/approvals/pending/count`, { headers })
      .then(r => setApprovals(r.data?.count || 0))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the active school name fresh (the BC "current company" indicator).
  // IMPORTANT: merge into the stored school rather than replace it —
  // PrivateRoute requires school.provisionStatus === 'Active' for staff, so
  // dropping that field here silently locked every non-admin out of all
  // modules (redirected to /school-pending) after the dashboard loaded.
  useEffect(() => {
    axios.get(`${API}/api/schools/current`, { headers })
      .then(r => {
        if (r.data) {
          setSchool(r.data);
          const prev = JSON.parse(localStorage.getItem('school') || '{}');
          localStorage.setItem('school', JSON.stringify({
            ...prev,
            id: r.data._id, code: r.data.code, name: r.data.name, county: r.data.county,
            provisionStatus: r.data.provisionStatus ?? prev.provisionStatus,
          }));
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSignOut = () => {
    ['token', 'user', 'school', 'permissions'].forEach(k => localStorage.removeItem(k));
    navigate('/login');
  };

  const k = kpis || {};

  // Quick-create columns the current user can actually use (empty ones dropped).
  const quickGroups = QUICK_CREATE
    .map(g => ({ ...g, items: g.items.filter(it => !it.module || canView(it.module)) }))
    .filter(g => g.items.length);
  const goCreate = (path) => { setQuickAnchor(null); navigate(path); };

  // Derived financial-snapshot figures.
  const bankTotal   = (banks || []).reduce((s, b) => s + (b.balance || 0), 0);
  const totalIncome = incStmt?.totals?.income  || 0;
  const totalExpense= incStmt?.totals?.expense || 0;
  const netProfit   = incStmt?.totals?.netProfit ?? (totalIncome - totalExpense);
  const barMax      = Math.max(totalIncome, totalExpense, 1);
  // Top expense categories → donut slices (5 named + rolled-up "Other").
  const expSorted   = [...(incStmt?.expense || [])].filter(e => e.amount > 0).sort((a, b) => b.amount - a.amount);
  const expSlices   = expSorted.slice(0, 5).map((e, i) => ({ label: e.accountName, value: e.amount, color: DONUT_COLORS[i] }));
  const expOther    = expSorted.slice(5).reduce((s, e) => s + e.amount, 0);
  if (expOther > 0) expSlices.push({ label: 'Other', value: expOther, color: DONUT_COLORS[5] });

  // ── Expense donut (self-contained SVG — no chart lib) ─────────────────────
  const Donut = ({ slices, size = 168, thickness = 24 }) => {
    const total = slices.reduce((s, x) => s + x.value, 0);
    const r = (size - thickness) / 2;
    const C = 2 * Math.PI * r;
    let acc = 0;
    return (
      <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#eceff1" strokeWidth={thickness} />
            {total > 0 && slices.map((s, i) => {
              const dash = (s.value / total) * C;
              const el = (
                <circle key={i} cx={size / 2} cy={size / 2} r={r} fill="none"
                  stroke={s.color} strokeWidth={thickness}
                  strokeDasharray={`${dash} ${C - dash}`} strokeDashoffset={-acc}
                  strokeLinecap="butt" />
              );
              acc += dash;
              return el;
            })}
          </g>
        </svg>
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="caption" color="text.secondary">Expenses</Typography>
          <Typography variant="subtitle1" fontWeight={800} lineHeight={1.1}>{fmt(total)}</Typography>
        </Box>
      </Box>
    );
  };

  // ── KPI Cue ──────────────────────────────────────────────────────────────
  const Cue = ({ icon: Icon, label, value, sub, color, urgent, path }) => (
    <Card elevation={2} sx={{
      borderRadius: 3, cursor: path ? 'pointer' : 'default', height: '100%',
      borderLeft: `4px solid ${urgent ? '#c62828' : color}`,
      transition: 'transform .15s, box-shadow .15s',
      '&:hover': path ? { boxShadow: 5, transform: 'translateY(-2px)' } : {}
    }} onClick={() => path && navigate(path)}>
      <CardContent sx={{ py: 1.8 }}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography variant="h4" fontWeight={800} lineHeight={1}
              color={urgent ? 'error.main' : color}>
              {loading ? '…' : value}
            </Typography>
            <Typography variant="body2" fontWeight={600} mt={0.5}>{label}</Typography>
            {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
          </Box>
          <Box sx={{
            width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: `${urgent ? '#c62828' : color}18`
          }}>
            <Icon sx={{ fontSize: 21, color: urgent ? '#c62828' : color }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  // ── Role Center Card ──────────────────────────────────────────────────────
  const RoleCard = ({ icon: Icon, title, desc, color, path, stats, disabled, soon }) => (
    <Card elevation={disabled ? 0 : 2} sx={{
      borderRadius: 3, height: '100%',
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.55 : 1,
      borderTop: `5px solid ${disabled ? '#ccc' : color}`,
      transition: 'transform .15s, box-shadow .15s',
      '&:hover': disabled ? {} : { boxShadow: 6, transform: 'translateY(-3px)' }
    }} onClick={() => !disabled && navigate(path)}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1.5} mb={1.2}>
          <Box sx={{
            width: 46, height: 46, borderRadius: 2, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: disabled ? '#f5f5f5' : `${color}15`
          }}>
            <Icon sx={{ fontSize: 24, color: disabled ? '#bbb' : color }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>{title}</Typography>
            {soon && <Chip label="Coming Soon" size="small" sx={{ height: 18, fontSize: 10, mt: 0.3 }} />}
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={1.5} sx={{ minHeight: 36 }}>{desc}</Typography>
        {stats && !disabled && (
          <Box display="flex" gap={1} flexWrap="wrap">
            {stats.map(s => (
              <Chip key={s.label} size="small" label={`${loading ? '…' : fmtN(s.value)} ${s.label}`}
                sx={{ height: 20, fontSize: 11, bgcolor: `${color}12`, color }} />
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f6f8', py: 3 }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3 } }}>

        {user?.impersonating && (
          <Box sx={{
            mb: 2, px: 2, py: 1, borderRadius: 2, bgcolor: '#4a148c', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1,
          }}>
            <Typography variant="body2" fontWeight={600}>
              🛠 Dev view — using {school?.name || 'this school'}'s database as super-admin
            </Typography>
            <Button size="small" variant="contained" color="secondary"
              onClick={() => navigate('/admin/schools')}>
              Back to Admin
            </Button>
          </Box>
        )}

        {/* Header */}
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          mb: 3, p: { xs: 2, sm: 3 }, borderRadius: 3,
          background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
          color: '#fff', boxShadow: 3, flexWrap: 'wrap', gap: 2
        }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {school?.name || 'EduMIS — Home'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.3 }}>
              {school?.name
                ? `EduMIS${school.county ? ` · ${school.county}` : ''}`
                : 'Education Management Information System'}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {quickGroups.length > 0 && (
              <Button
                variant="contained" startIcon={<Add />}
                onClick={e => setQuickAnchor(e.currentTarget)}
                sx={{
                  bgcolor: '#fff', color: '#0d47a1', fontWeight: 700, borderRadius: 2,
                  boxShadow: 2, textTransform: 'none', px: 1.8,
                  '&:hover': { bgcolor: '#f0f4ff' },
                }}>
                Create
              </Button>
            )}
            <Tooltip title="Search pages, lists & reports (Ctrl/⌘+K)">
              <IconButton onClick={openGlobalSearch}
                sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' } }}>
                <Search />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh KPIs">
              <IconButton onClick={fetchKpis} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' } }}>
                {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <Refresh />}
              </IconButton>
            </Tooltip>
            <Tooltip title={approvals ? `${approvals} awaiting your approval` : 'My Approvals'}>
              <IconButton onClick={() => navigate('/approvals')}
                sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' } }}>
                <CountBadge badgeContent={approvals} color="error" max={99}>
                  <FactCheck />
                </CountBadge>
              </IconButton>
            </Tooltip>
            <Tooltip title={unread ? `${unread} unread message${unread !== 1 ? 's' : ''}` : 'Messages'}>
              <IconButton onClick={() => navigate('/messages')}
                sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' } }}>
                <CountBadge badgeContent={unread} color="error" max={99}>
                  <MailOutline />
                </CountBadge>
              </IconButton>
            </Tooltip>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Welcome, {user?.fullName || user?.email}
            </Typography>
            <IconButton onClick={e => setAnchorEl(e.currentTarget)} sx={{ color: '#fff' }}>
              <AccountCircle sx={{ fontSize: 38 }} />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
              <MenuItem disabled><Person sx={{ mr: 1, fontSize: 18 }} />{user?.username}</MenuItem>
              <MenuItem disabled><PersonalVideoTwoTone sx={{mr:1, fontSize: 18 }} />{user?.fullName}</MenuItem>
              <MenuItem disabled><Badge sx={{ mr: 1, fontSize: 18 }} />{user?.role === 'admin' ? 'Administrator' : user?.role === 'parent' ? 'Parent' : 'Staff User'}</MenuItem>
              <Divider />
              <MenuItem onClick={handleSignOut}>Sign Out</MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Quick-Create mega-menu */}
        <Popover
          open={Boolean(quickAnchor)} anchorEl={quickAnchor} onClose={() => setQuickAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ sx: { borderRadius: 3, mt: 1, p: 2.5, maxWidth: 780, boxShadow: 8 } }}>
          <Typography variant="overline" color="text.secondary" fontWeight={700}>Create new</Typography>
          <Box sx={{
            display: 'grid', gap: 2.5, mt: 0.5,
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(150px, 1fr))', md: 'repeat(3, minmax(150px, 1fr))' },
          }}>
            {quickGroups.map(group => (
              <Box key={group.title}>
                <Typography variant="caption" fontWeight={800}
                  sx={{ color: group.color, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                  {group.title}
                </Typography>
                {group.items.map(item => (
                  <ButtonBase key={item.path} onClick={() => goCreate(item.path)}
                    sx={{
                      display: 'flex', justifyContent: 'flex-start', width: '100%',
                      px: 1, py: 0.6, borderRadius: 1.5, gap: 0.75,
                      '&:hover': { bgcolor: `${group.color}12` },
                    }}>
                    <ChevronRight sx={{ fontSize: 16, color: group.color }} />
                    <Typography variant="body2" fontWeight={500}>{item.label}</Typography>
                  </ButtonBase>
                ))}
              </Box>
            ))}
          </Box>
        </Popover>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* BC-style module bar — click a module to expand its groups & pages */}
        <TopModuleBar modules={TOP_MODULES.filter(m => canView(m.permission))} />

        {/* Activities */}
        <Box mb={3}>
          <Box display="flex" alignItems="center" mb={1.5}>
            <Typography variant="subtitle1" fontWeight={700}>Activities</Typography>
            <Divider sx={{ flex: 1, ml: 1.5 }} />
          </Box>
          <Grid container spacing={2}>
            {user?.role === 'classTeacher' && (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <Cue icon={People} label="My Learners" color="#1565c0"
                    value={ctStats ? fmtN(ctStats.learners) : '…'}
                    sub={ctStats?.className || ''} path="/class-teacher-centre" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Cue icon={AccountBalanceWallet} label="Class Fee Balance"
                    color={ctStats?.balance > 0 ? '#c62828' : '#2e7d32'}
                    urgent={ctStats?.balance > 0}
                    value={ctStats?.balance != null ? `${sym()} ${fmt(ctStats.balance)}` : '…'}
                    path="/class-teacher-centre" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Cue icon={TrendingUp} label="Exam Results" color="#6a1b9a"
                    value={ctStats ? fmtN(ctStats.results) : '…'}
                    sub="records this class" path="/class-teacher-centre" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Cue icon={School} label="My Classes" color="#00838f"
                    value={ctStats ? fmtN(ctStats.classCount) : '…'}
                    sub="assigned to you" path="/class-teacher-centre" />
                </Grid>
              </>
            )}
            {canView('academics') && (
              <Grid item xs={12} sm={6} md={2}>
                <Cue icon={People} label="Learners" color="#1565c0"
                  value={fmtN(k.learners)} path="/learners" />
              </Grid>
            )}
            {canView('finance') && (
              <>
                <Grid item xs={12} sm={6} md={2}>
                  <Cue icon={PendingActions} label="Open Invoices" color="#f57c00"
                    value={fmtN(k.openInv)}
                    sub={k.openInvAmt ? `${sym()} ${fmt(k.openInvAmt)}` : ''}
                    path="/invoices" />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Cue icon={Warning} label="Overdue" color="#c62828"
                    urgent={k.overdue > 0}
                    value={fmtN(k.overdue)}
                    sub="Unpaid past due date" path="/invoices" />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Cue icon={Receipt} label="Receipts / Month" color="#2e7d32"
                    value={fmtN(k.monthRecCount)}
                    sub={k.monthRecAmt ? `${sym()} ${fmt(k.monthRecAmt)}` : ''}
                    path="/receipts" />
                </Grid>
              </>
            )}
            {canView('supply-chain') && (
              <>
                <Grid item xs={12} sm={6} md={2}>
                  <Cue icon={Description} label="Open Purchase Inv." color="#6a1b9a"
                    value={fmtN(k.openPinv)}
                    sub={k.openPinvAmt ? `${sym()} ${fmt(k.openPinvAmt)} to pay` : 'Awaiting payment'}
                    path="/payables/invoices" />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Cue icon={TrendingUp} label="Pending Vouchers" color="#00695c"
                    value={fmtN(k.pendingPv)} path="/payables/vouchers" />
                </Grid>
              </>
            )}
          </Grid>
        </Box>

        {/* Financial Snapshot — Income vs Expense + Bank Accounts */}
        {canView('finance') && (
          <Box mb={3}>
            <Box display="flex" alignItems="center" mb={1.5} gap={1}>
              <Typography variant="subtitle1" fontWeight={700}>Financial Snapshot</Typography>
              {finLoading && <CircularProgress size={15} />}
              <Divider sx={{ flex: 1, mx: 1 }} />
              <Chip label="This month" size="small" clickable
                onClick={() => setPeriod('month')}
                color={period === 'month' ? 'primary' : 'default'}
                variant={period === 'month' ? 'filled' : 'outlined'} />
              <Chip label="This year" size="small" clickable
                onClick={() => setPeriod('year')}
                color={period === 'year' ? 'primary' : 'default'}
                variant={period === 'year' ? 'filled' : 'outlined'} />
            </Box>
            <Grid container spacing={2.5}>
              {/* Income vs Expense */}
              <Grid item xs={12} md={7}>
                <Card elevation={2} sx={{ borderRadius: 3, height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={1.5}>
                      Income &amp; Expense · {period === 'month' ? 'This month' : 'Year to date'}
                    </Typography>
                    <Box display="flex" gap={2.5} flexWrap="wrap" alignItems="center">
                      <Donut slices={expSlices} />
                      <Box flex={1} minWidth={200}>
                        {/* Income bar */}
                        <Box display="flex" alignItems="center" gap={0.75} mb={0.3}>
                          <TrendingUp sx={{ fontSize: 18, color: '#2e7d32' }} />
                          <Typography variant="body2" fontWeight={600}>Income</Typography>
                          <Box flex={1} />
                          <Typography variant="body2" fontWeight={800} color="#2e7d32">{sym()} {fmt(totalIncome)}</Typography>
                        </Box>
                        <Box sx={{ height: 10, borderRadius: 5, bgcolor: '#e8f5e9', mb: 1.5, overflow: 'hidden' }}>
                          <Box sx={{ height: '100%', width: `${(totalIncome / barMax) * 100}%`, bgcolor: '#2e7d32' }} />
                        </Box>
                        {/* Expense bar */}
                        <Box display="flex" alignItems="center" gap={0.75} mb={0.3}>
                          <TrendingDown sx={{ fontSize: 18, color: '#c62828' }} />
                          <Typography variant="body2" fontWeight={600}>Expense</Typography>
                          <Box flex={1} />
                          <Typography variant="body2" fontWeight={800} color="#c62828">{sym()} {fmt(totalExpense)}</Typography>
                        </Box>
                        <Box sx={{ height: 10, borderRadius: 5, bgcolor: '#ffebee', mb: 1.5, overflow: 'hidden' }}>
                          <Box sx={{ height: '100%', width: `${(totalExpense / barMax) * 100}%`, bgcolor: '#c62828' }} />
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Box display="flex" alignItems="center">
                          <Typography variant="body2" fontWeight={600}>Net {netProfit >= 0 ? 'Surplus' : 'Deficit'}</Typography>
                          <Box flex={1} />
                          <Typography variant="subtitle1" fontWeight={800} color={netProfit >= 0 ? '#2e7d32' : '#c62828'}>
                            {sym()} {fmt(Math.abs(netProfit))}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    {/* Expense legend */}
                    {expSlices.length > 0 && (
                      <Box display="flex" flexWrap="wrap" gap={1.2} mt={1.5}>
                        {expSlices.map(s => (
                          <Box key={s.label} display="flex" alignItems="center" gap={0.5}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color }} />
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 140 }}>
                              {s.label}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                    {!finLoading && expSlices.length === 0 && totalIncome === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        No posted income or expense for this period yet.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              {/* Bank Accounts */}
              <Grid item xs={12} md={5}>
                <Card elevation={2} sx={{ borderRadius: 3, height: '100%' }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={1.5}>
                      <Typography variant="subtitle2" fontWeight={700} color="text.secondary">Bank Accounts</Typography>
                      <Box flex={1} />
                      <Typography variant="caption" color="text.secondary">As of today</Typography>
                    </Box>
                    {(banks || []).length === 0 && !finLoading && (
                      <Typography variant="body2" color="text.secondary">No bank or cash accounts set up.</Typography>
                    )}
                    {(banks || []).map(b => (
                      <ButtonBase key={b.accountNo} onClick={() => navigate(`/banks/${b.accountNo}`)}
                        sx={{ display: 'flex', width: '100%', textAlign: 'left', px: 1, py: 1, borderRadius: 2,
                          '&:hover': { bgcolor: '#f4f6f8' } }}>
                        <Box sx={{ width: 34, height: 34, borderRadius: '50%', bgcolor: '#00838f18',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.2, flexShrink: 0 }}>
                          <AccountBalance sx={{ fontSize: 18, color: '#00838f' }} />
                        </Box>
                        <Box minWidth={0} flex={1}>
                          <Typography variant="body2" fontWeight={700} noWrap>
                            {b.accountNo} {b.accountName || b.bankName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>{b.bankName}</Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={800}
                          color={b.balance < 0 ? '#c62828' : 'text.primary'} sx={{ ml: 1 }}>
                          {sym()} {fmt(b.balance)}
                        </Typography>
                      </ButtonBase>
                    ))}
                    {(banks || []).length > 0 && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Box display="flex" alignItems="center" px={1}>
                          <Typography variant="body2" fontWeight={700}>Total cash &amp; bank</Typography>
                          <Box flex={1} />
                          <Typography variant="subtitle1" fontWeight={800} color="#00838f">{sym()} {fmt(bankTotal)}</Typography>
                        </Box>
                        <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate('/banks')}
                          sx={{ mt: 1, textTransform: 'none' }}>
                          View all bank accounts
                        </Button>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Role Centers */}
        <Box mb={3}>
          <Box display="flex" alignItems="center" mb={1.5}>
            <Typography variant="subtitle1" fontWeight={700}>Role Centers</Typography>
            <Divider sx={{ flex: 1, ml: 1.5 }} />
          </Box>
          <Grid container spacing={2.5}>
            {user?.role === 'classTeacher' && (
              <Grid item xs={12} sm={6} md={3}>
                <RoleCard icon={School} title="Class Teacher Centre" color="#1565c0"
                  path="/class-teacher-centre"
                  desc="Your class learners, biodata, fee balances, results and performance." />
              </Grid>
            )}
            {canView('finance') && (
              <Grid item xs={12} sm={6} md={3}>
                <RoleCard icon={AccountBalanceWallet} title="Finance" color="#1565c0"
                  path="/finance"
                  desc="General Ledger, Receivables, Payables, Banks and GL entries."
                  stats={[
                    { label: 'open invoices', value: k.openInv },
                    { label: 'pending PV',    value: k.pendingPv },
                  ]} />
              </Grid>
            )}
            {canView('academics') && (
              <Grid item xs={12} sm={6} md={3}>
                <RoleCard icon={School} title="Academics" color="#2e7d32"
                  path="/academics"
                  desc="Academic years, stages, classes, fee templates and learner management."
                  stats={[
                    { label: 'learners', value: k.learners },
                    { label: 'classes',  value: k.classes  },
                  ]} />
              </Grid>
            )}
            {canView('supply-chain') && (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <RoleCard icon={LocalShipping} title="Supply Chain" color="#e65100"
                    path="/supply-chain"
                    desc="Vendors, purchase orders, stores, requisitions and stock transfers."
                    stats={[
                      { label: 'open PO',   value: k.openPinv  },
                      { label: 'vouchers',  value: k.pendingPv },
                    ]} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <RoleCard icon={HomeWork} title="Fixed Assets" color="#6a1b9a"
                    path="/fixed-assets"
                    desc="Asset register, depreciation, FA classes and posting groups." />
                </Grid>
              </>
            )}
            {canView('hr') && (
              <Grid item xs={12} sm={6} md={3}>
                <RoleCard icon={Groups} title="Human Resources" color="#546e7a"
                  path="/hr"
                  stats={k.employees != null ? [{ label: 'Active Staff', value: k.employees }] : undefined}
                  desc="Employee records, leave management and contracts." />
              </Grid>
            )}
            {canView('payroll') && (
              <Grid item xs={12} sm={6} md={3}>
                <RoleCard icon={MonetizationOn} title="Payroll" color="#78909c"
                  path="/payroll"
                  desc="Payroll runs, payslips, deductions and PAYE." />
              </Grid>
            )}
            {canView('setups') && (
              <Grid item xs={12} sm={6} md={3}>
                <RoleCard icon={Settings} title="Setups" color="#455a64"
                  path="/setups"
                  desc="Number series, locations, posting setup and system configuration." />
              </Grid>
            )}
          </Grid>
        </Box>

      </Box>
    </Box>
  );
};

export default Dashboard;
